import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AutoResizeTextarea } from "./AutoResizeTextarea";
import {
  createOneOnOneMeeting,
  deleteOneOnOneMeeting,
  fetchOneOnOneMeetings,
  fetchOneOnOneTemplates,
  updateOneOnOneMeeting,
  type OneOnOneMeeting,
  type OneOnOneTemplate,
  type OneOnOneTemplateField,
  type OneOnOneFollowUpTaskInput,
} from "../lib/api";
import { meetingNumberFor, downloadOneOnOneMeetingPdf, printOneOnOneMeeting } from "../lib/one-on-one-print";
import { oneOnOneDisplayDate, oneOnOneDisplayDateMs } from "../lib/one-on-one-dates";
import {
  ASSOCIATE_FEEDBACK_FIELD_ID,
  ASSOCIATE_FEEDBACK_LABEL,
  formatAssociateResponseDisplay,
  formatYesNoResponseDisplay,
} from "../lib/one-on-one-feedback";
import {
  appendLeaderCommentsIfMissing,
  findLeaderCommentsField,
  isLeaderCommentsEmpty,
  LEADER_COMMENTS_NUDGE_COPY,
  LEADER_COMMENTS_NUDGE_TITLE,
} from "../lib/check-in-leader-comments";
import {
  checkInEditMenuLabel,
  canPrintCheckIn,
  getOneOnOneMeetingStatusFromMeeting,
  oneOnOneMeetingStatusClass,
  oneOnOneMeetingStatusLabel,
} from "../lib/one-on-one-status";
import { SenecaPrepCard } from "./seneca/SenecaPrepCard";
import { SenecaCheckInPanel, type SenecaFollowUpSuggestion } from "./seneca/SenecaCheckInPanel";
import { SenecaSummaryModal } from "./seneca/SenecaSummaryModal";
import { DevelopmentPlanGenerator } from "./seneca/DevelopmentPlanGenerator";
import { fetchSenecaPrep, type SenecaDevelopmentGoalDraft, type SenecaPrep } from "../lib/seneca-api";
import { EnterprisePageLoading } from "./EnterprisePageLoading";

const FIELD_TYPE_LABELS: Record<string, string> = {
  section: "Section",
  short_text: "Short answer",
  long_text: "Long answer",
  rating: "Rating",
  yes_no: "Yes or No",
  manager_notes: "Leader comments",
  associate_notes: "Associate notes",
};

function fieldTypeLabel(type: string): string {
  return FIELD_TYPE_LABELS[type] ?? type;
}

type PreviewSectionGroup = {
  section: OneOnOneTemplateField;
  fields: OneOnOneTemplateField[];
};

function groupPreviewSections(fields: OneOnOneTemplateField[]): PreviewSectionGroup[] {
  const sorted = [...fields].sort((a, b) => a.order - b.order);
  const groups: PreviewSectionGroup[] = [];
  let current: PreviewSectionGroup | null = null;

  for (const field of sorted) {
    if (field.type === "section") {
      current = { section: field, fields: [] };
      groups.push(current);
    } else if (field.type === "associate_notes") {
      continue;
    } else if (current) {
      current.fields.push(field);
    } else {
      current = {
        section: {
          id: "__preview_general__",
          label: "Responses",
          type: "section",
          order: 0,
        },
        fields: [field],
      };
      groups.push(current);
    }
  }

  return groups;
}

function renderPreviewQuestion(field: OneOnOneTemplateField, answer: string | number | undefined) {
  if (answer === undefined || answer === "" || answer === 0) return null;
  const display =
    field.type === "yes_no" ? formatYesNoResponseDisplay(answer) : formatAssociateResponseDisplay(answer);
  return (
    <div key={field.id} className="enterprise-oneone-preview-question">
      <div className="enterprise-oneone-preview-question-label">{field.label}</div>
      <div className="enterprise-oneone-preview-question-answer">{display}</div>
    </div>
  );
}

function formatMeetingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function meetingToFillTemplate(meeting: OneOnOneMeeting): OneOnOneTemplate {
  return {
    id: meeting.templateId ?? meeting.id,
    teamId: meeting.teamId,
    title: meeting.templateTitle,
    description: null,
    fields: meeting.templateFields,
    createdById: meeting.createdById,
    createdAt: meeting.createdAt,
    updatedAt: meeting.createdAt,
    createdBy: meeting.createdBy,
  };
}

type Props = {
  teamId: string;
  memberUserId: string;
  memberName: string;
  managerName: string | null;
  leaderUserId: string | null;
  canCreate: boolean;
  canModify: boolean;
  activeDevGoals?: number;
  completedDevGoals?: number;
  daysSinceLastCheckIn?: number | null;
  nextCheckInValue?: string;
  nextCheckInHint?: string;
  teamName?: string;
  isSelf?: boolean;
};

type View = "list" | "pick" | "fill";

/** iPad and smaller: check-in opens full screen automatically. */
const CHECKIN_COMPACT_MAX_WIDTH = 1024;

function useCompactCheckInLayout() {
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${CHECKIN_COMPACT_MAX_WIDTH}px)`).matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${CHECKIN_COMPACT_MAX_WIDTH}px)`);
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return compact;
}

type FollowUpDraft = {
  id: string;
  title: string;
  assigneeRole: "associate" | "leader";
  dueDate: string;
};

function newFollowUpDraft(): FollowUpDraft {
  return { id: crypto.randomUUID(), title: "", assigneeRole: "associate", dueDate: "" };
}

function dueDateInputToIso(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match?.[1] ? match[0] : undefined;
}

function formatFollowUpDueDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function assigneeDisplayName(
  userId: string | undefined,
  memberUserId: string,
  memberName: string,
  _leaderUserId: string | null,
  leaderName: string | null,
): string {
  if (!userId || userId === memberUserId) return memberName;
  return leaderName ?? "Leader";
}

function CheckInIllustration() {
  return (
    <svg width="40" height="40" viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="24" r="24" fill="#ede9fe" />
      <rect x="14" y="17" width="20" height="17" rx="3" stroke="#7c3aed" strokeWidth="2" />
      <path d="M14 22h20" stroke="#7c3aed" strokeWidth="2" />
      <path d="M19 13v5M29 13v5" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" />
      <circle cx="19" cy="28" r="1.25" fill="#c4b5fd" />
      <circle cx="24" cy="28" r="1.25" fill="#c4b5fd" />
      <circle cx="29" cy="28" r="1.25" fill="#c4b5fd" />
    </svg>
  );
}

type CheckInGrowCardProps = {
  canCreate: boolean;
  onCreate?: () => void;
  loading?: boolean;
  title?: string;
  copy?: string;
  buttonLabel?: string;
};

function CheckInGrowCard({
  canCreate,
  onCreate,
  loading = false,
  title = "Keep checking in",
  copy,
  buttonLabel = "New check-in",
}: CheckInGrowCardProps) {
  const defaultCopy = canCreate
    ? "Regular check-ins help track progress, share feedback, and follow up on goals."
    : "Check-ins will appear here once your manager records them.";

  return (
    <div className="enterprise-dev-plan-grow">
      <CheckInIllustration />
      <p className="enterprise-dev-plan-grow-title">{title}</p>
      <p className="enterprise-dev-plan-grow-copy">{copy ?? defaultCopy}</p>
      {canCreate && onCreate ? (
        <button
          type="button"
          className="enterprise-dev-plan-grow-btn"
          disabled={loading}
          onClick={onCreate}
        >
          {loading ? "Loading…" : buttonLabel}
        </button>
      ) : null}
    </div>
  );
}

function MeetingStatusBadge({ meeting }: { meeting: OneOnOneMeeting }) {
  if (meeting.status === "draft") {
    return (
      <span className="enterprise-oneone-status enterprise-oneone-status--draft" title="Draft">
        Draft
      </span>
    );
  }
  const status = getOneOnOneMeetingStatusFromMeeting(meeting);
  return (
    <span className={oneOnOneMeetingStatusClass(status)} title={oneOnOneMeetingStatusLabel(status)}>
      {oneOnOneMeetingStatusLabel(status)}
    </span>
  );
}

/** Visual status for enterprise check-ins list (maps existing helpers → On track / Needs attention). */
function CheckInListStatusBadge({ meeting }: { meeting: OneOnOneMeeting }) {
  if (meeting.status === "draft") {
    return (
      <span className="enterprise-checkins-status enterprise-checkins-status--draft" title="Draft">
        Draft
      </span>
    );
  }
  const status = getOneOnOneMeetingStatusFromMeeting(meeting);
  const needsAttention = status === "open";
  return (
    <span
      className={`enterprise-checkins-status${needsAttention ? " enterprise-checkins-status--attention" : " enterprise-checkins-status--on-track"}`}
      title={oneOnOneMeetingStatusLabel(status)}
    >
      {needsAttention ? "Needs attention" : "On track"}
    </span>
  );
}

function formatRelativeDaysAgo(iso: string): string {
  try {
    const then = new Date(iso);
    const now = new Date();
    const startToday = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const startThen = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
    const days = Math.max(0, Math.floor((startToday - startThen) / 86_400_000));
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  } catch {
    return "";
  }
}

function goalsReviewedLabel(meeting: OneOnOneMeeting): string {
  const title = meeting.templateTitle?.trim();
  if (title) return title;
  const section = meeting.templateFields?.find((f) => f.type === "section" && f.label?.trim());
  return section?.label?.trim() || "Check-in";
}

function countFeedbackThisMonth(meetings: OneOnOneMeeting[]): number {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  return meetings.filter((m) => {
    if (m.status === "draft") return false;
    const feedback = m.responses?.[ASSOCIATE_FEEDBACK_FIELD_ID];
    if (feedback == null || feedback === "" || feedback === 0) return false;
    try {
      const d = new Date(oneOnOneDisplayDate(m));
      return d.getMonth() === month && d.getFullYear() === year;
    } catch {
      return false;
    }
  }).length;
}

function IconCheckinsHeader() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function IconMetricCalendar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
    </svg>
  );
}

function IconMetricTarget() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconMetricChat() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M4 5h16v11H8l-4 4V5Z" />
    </svg>
  );
}

function IconExport() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M12 3v12" />
      <path d="m8 11 4 4 4-4" />
      <path d="M5 19h14" />
    </svg>
  );
}

function IconWhyStar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 3.5 14.4 9l6 .5-4.6 4 1.4 5.8L12 16.8 6.8 19.3 8.2 13.5 3.6 9.5l6-.5L12 3.5Z" />
    </svg>
  );
}

function IconSummaryChart() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M4 19h16" />
      <path d="M7 16V9M12 16V5M17 16v-4" />
    </svg>
  );
}

function IconSummaryClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.5l3 1.5" />
    </svg>
  );
}

function IconSummaryPeople() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3.5 19c1-3 2.8-4.5 5.5-4.5S13 16 14 19" />
      <path d="M14.5 14.5c1.6 0 3 .7 4 2.2" />
    </svg>
  );
}

function IconHistoryCheck({ attention }: { attention?: boolean }) {
  return (
    <span
      className={`enterprise-checkins-history-check${attention ? " is-attention" : ""}`}
      aria-hidden
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
        <path d="m6 12 4 4 8-8" />
      </svg>
    </span>
  );
}

export function OneOnOneHistoryTab({
  teamId,
  memberUserId,
  memberName,
  managerName,
  leaderUserId,
  canCreate,
  canModify,
  activeDevGoals,
  completedDevGoals,
  daysSinceLastCheckIn,
  nextCheckInValue,
  nextCheckInHint,
  teamName,
  isSelf = false,
}: Props) {
  const [view, setView] = useState<View>("list");
  const [meetings, setMeetings] = useState<OneOnOneMeeting[]>([]);
  const [templates, setTemplates] = useState<OneOnOneTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<OneOnOneTemplate | null>(null);
  const [editingMeeting, setEditingMeeting] = useState<OneOnOneMeeting | null>(null);
  const [responses, setResponses] = useState<Record<string, string | number>>({});
  const [previewMeeting, setPreviewMeeting] = useState<OneOnOneMeeting | null>(null);
  const [menuMeetingId, setMenuMeetingId] = useState<string | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [followUpDrafts, setFollowUpDrafts] = useState<FollowUpDraft[]>([]);
  const [feedbackPromptOpen, setFeedbackPromptOpen] = useState(false);
  const [leaderCommentsNudgeOpen, setLeaderCommentsNudgeOpen] = useState(false);
  const [highlightLeaderFieldId, setHighlightLeaderFieldId] = useState<string | null>(null);
  const [userExpandedFullscreen, setUserExpandedFullscreen] = useState(false);
  const [listNotice, setListNotice] = useState<string | null>(null);
  const [prepAcknowledged, setPrepAcknowledged] = useState(false);
  const [senecaSummaryOpen, setSenecaSummaryOpen] = useState(false);
  const [senecaSummaryPayload, setSenecaSummaryPayload] = useState<{
    templateTitle: string;
    templateFields: OneOnOneTemplate["fields"];
    responses: Record<string, string | number>;
    followUpTasks: SenecaFollowUpSuggestion[];
  } | null>(null);
  const [senecaDevPlanOpen, setSenecaDevPlanOpen] = useState(false);
  const [senecaDevPlanDraft, setSenecaDevPlanDraft] = useState<SenecaDevelopmentGoalDraft | null>(null);
  const [senecaFocusedFieldId, setSenecaFocusedFieldId] = useState<string | null>(null);
  const [senecaPrep, setSenecaPrep] = useState<SenecaPrep | null>(null);
  const [senecaPrepLoading, setSenecaPrepLoading] = useState(false);
  const [senecaPrepErr, setSenecaPrepErr] = useState<string | null>(null);
  const [senecaPrepRequested, setSenecaPrepRequested] = useState(false);
  const compactCheckInLayout = useCompactCheckInLayout();
  const checkInFullscreen = compactCheckInLayout || userExpandedFullscreen;

  const resolveLeaderUserId = (meeting?: OneOnOneMeeting | null) =>
    leaderUserId ?? meeting?.createdById ?? null;

  const buildFollowUpPayload = (meeting?: OneOnOneMeeting | null): OneOnOneFollowUpTaskInput[] => {
    const leaderId = resolveLeaderUserId(meeting);
    return followUpDrafts
      .map((draft) => ({
        draft,
        title: draft.title.trim(),
      }))
      .filter((item) => item.title.length > 0)
      .map(({ draft, title }) => {
        const dueDate = dueDateInputToIso(draft.dueDate);
        return {
          title,
          assigneeUserId: draft.assigneeRole === "associate" ? memberUserId : leaderId ?? memberUserId,
          ...(dueDate ? { dueDate } : {}),
        };
      });
  };

  const loadMeetings = useCallback(async () => {
    if (!teamId || !memberUserId) return;
    setLoadingMeetings(true);
    setErr(null);
    try {
      const list = await fetchOneOnOneMeetings(teamId, memberUserId);
      setMeetings(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load check-in history.");
    } finally {
      setLoadingMeetings(false);
    }
  }, [memberUserId, teamId]);

  useEffect(() => {
    void loadMeetings();
  }, [loadMeetings]);

  useEffect(() => {
    setMeetings([]);
    setView("list");
    setSelectedTemplate(null);
    setEditingMeeting(null);
    setResponses({});
    setFollowUpDrafts([]);
    setPreviewMeeting(null);
    setMenuMeetingId(null);
    setErr(null);
    setTemplates([]);
  }, [memberUserId, teamId]);

  const startCreate = useCallback(async () => {
    if (!teamId) {
      setErr("No workspace selected.");
      return;
    }
    setErr(null);
    setEditingMeeting(null);
    setLoadingTemplates(true);
    setView("pick");
    try {
      const list = await fetchOneOnOneTemplates(teamId);
      setTemplates(list);
      if (list.length === 0) {
        setErr("No check-in templates yet. The workspace owner can create templates from the Team page.");
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load templates.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [teamId]);

  const pickTemplate = (template: OneOnOneTemplate) => {
    const fields = appendLeaderCommentsIfMissing(template.fields);
    const withLeaderComments = { ...template, fields };
    setSelectedTemplate(withLeaderComments);
    setEditingMeeting(null);
    const initial: Record<string, string | number> = {};
    for (const field of fields) {
      if (field.type === "section") continue;
      if (field.type === "rating") initial[field.id] = 0;
      else initial[field.id] = "";
    }
    setResponses(initial);
    setFollowUpDrafts([newFollowUpDraft()]);
    setPrepAcknowledged(false);
    setSenecaPrep(null);
    setSenecaPrepErr(null);
    setSenecaPrepLoading(false);
    setSenecaPrepRequested(false);
    setErr(null);
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(false);
    setView("fill");
  };

  const requestSenecaPrep = useCallback(() => {
    if (!selectedTemplate || senecaPrepLoading) return;

    setSenecaPrepRequested(true);
    setSenecaPrepLoading(true);
    setSenecaPrepErr(null);
    void fetchSenecaPrep(teamId, memberUserId, {
      templateId: selectedTemplate.id,
      memberName,
      managerName,
    })
      .then((res) => setSenecaPrep(res.prep))
      .catch((e) => {
        setSenecaPrepErr(e instanceof Error ? e.message : "Could not load Seneca prep.");
        setSenecaPrep(null);
      })
      .finally(() => setSenecaPrepLoading(false));
  }, [selectedTemplate, senecaPrepLoading, teamId, memberUserId, memberName, managerName]);

  useEffect(() => {
    if (!menuMeetingId) return;
    const closeMenu = () => setMenuMeetingId(null);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [menuMeetingId]);

  useEffect(() => {
    if (view === "list") setUserExpandedFullscreen(false);
  }, [view]);

  useEffect(() => {
    if (!checkInFullscreen || (view !== "fill" && view !== "pick")) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [checkInFullscreen, view]);

  type CheckInShellConfig = {
    title: string;
    subtitle?: string;
    onBack: () => void;
    backLabel: string;
    footer?: ReactNode;
    headerAside?: ReactNode;
  };

  const renderCheckInShell = (content: ReactNode, config: CheckInShellConfig) => {
    const { title, subtitle, onBack, backLabel, footer, headerAside } = config;
    const footerNode = footer ?? null;

    if (checkInFullscreen) {
      return createPortal(
        <div className="enterprise-oneone-fill-overlay enterprise-oneone-checkin-open" role="dialog" aria-modal="true" aria-label={title}>
          <header className="enterprise-oneone-fill-header">
            <div className="enterprise-oneone-fill-header-toolbar">
              <button type="button" className="enterprise-oneone-fill-header-back" onClick={onBack}>
                ← {backLabel}
              </button>
              {!compactCheckInLayout ? (
                <button
                  type="button"
                  className="enterprise-oneone-fill-expand-btn"
                  onClick={() => setUserExpandedFullscreen(false)}
                >
                  Exit full screen
                </button>
              ) : null}
            </div>
            <div className="enterprise-oneone-fill-header-main">
              <div className="enterprise-oneone-fill-header-text">
                <h2 className="enterprise-oneone-fill-header-title">{title}</h2>
                {subtitle ? <p className="enterprise-muted enterprise-oneone-fill-header-sub">{subtitle}</p> : null}
              </div>
              {headerAside ? <div className="enterprise-oneone-fill-header-aside">{headerAside}</div> : null}
            </div>
          </header>
          <div className="enterprise-oneone-fill-body">{content}</div>
          {footerNode ? <footer className="enterprise-oneone-fill-footer">{footerNode}</footer> : null}
        </div>,
        document.body,
      );
    }

    return (
      <div className="enterprise-oneone-history enterprise-oneone-checkin-open">
        <div className="enterprise-oneone-fill-inline-head">
          <button type="button" className="enterprise-oneone-history-back" onClick={onBack}>
            ← {backLabel}
          </button>
          <div className="enterprise-oneone-fill-inline-head-row">
            <div className="enterprise-oneone-fill-inline-head-text">
              <h3 className="enterprise-oneone-history-heading">{title}</h3>
              {subtitle ? <p className="enterprise-muted enterprise-oneone-history-sub">{subtitle}</p> : null}
            </div>
            <div className="enterprise-oneone-fill-inline-head-actions">
              {headerAside}
              <button
                type="button"
                className="enterprise-oneone-fill-expand-btn"
                onClick={() => setUserExpandedFullscreen(true)}
              >
                Full screen
              </button>
            </div>
          </div>
        </div>
        {content}
        {footerNode}
      </div>
    );
  };

  const startEdit = (meeting: OneOnOneMeeting) => {
    setPreviewMeeting(null);
    setMenuMeetingId(null);
    setEditingMeeting(meeting);
    setSelectedTemplate(meetingToFillTemplate(meeting));
    setResponses({ ...meeting.responses });
    setFollowUpDrafts([]);
    setPrepAcknowledged(true);
    setErr(null);
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(false);
    setView("fill");
  };

  const setFieldValue = (fieldId: string, value: string | number) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
    if (highlightLeaderFieldId === fieldId && String(value).trim()) {
      setHighlightLeaderFieldId(null);
    }
  };

  const normalizeResponses = (fields: OneOnOneTemplateField[]) => {
    const normalized: Record<string, string | number> = {};
    for (const field of fields) {
      if (field.type === "section" || field.type === "associate_notes") continue;
      const raw = responses[field.id];
      if (field.type === "rating") {
        normalized[field.id] = typeof raw === "number" ? raw : Number(raw) || 0;
      } else {
        normalized[field.id] = typeof raw === "string" ? raw : String(raw ?? "");
      }
    }
    return normalized;
  };

  const performSave = async (requestAssociateFeedback: boolean) => {
    if (!selectedTemplate) return;
    setFeedbackPromptOpen(false);
    setSaving(true);
    setErr(null);
    try {
      const normalized = normalizeResponses(selectedTemplate.fields);
      const followUpTasks = buildFollowUpPayload(editingMeeting);
      const payload = {
        responses: normalized,
        followUpTasks,
        requestAssociateFeedback,
        status: "published" as const,
      };
      if (editingMeeting) {
        await updateOneOnOneMeeting(teamId, memberUserId, editingMeeting.id, payload);
      } else {
        await createOneOnOneMeeting(teamId, memberUserId, {
          templateId: selectedTemplate.id,
          ...payload,
        });
      }
      await loadMeetings();
      if (canCreate) {
        setSenecaSummaryPayload({
          templateTitle: selectedTemplate.title,
          templateFields: selectedTemplate.fields,
          responses: normalized,
          followUpTasks: followUpTasks.map((t) => ({
            title: t.title,
            assigneeRole: t.assigneeUserId === memberUserId ? "associate" : "leader",
          })),
        });
        setSenecaSummaryOpen(true);
        setSelectedTemplate(null);
        setEditingMeeting(null);
        setResponses({});
        setFollowUpDrafts([]);
        setListNotice(null);
      } else {
        finishCheckInToList();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save check-in.");
    } finally {
      setSaving(false);
    }
  };

  const performSaveDraft = async () => {
    if (!selectedTemplate || saving) return;
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(false);
    setSaving(true);
    setErr(null);
    try {
      const normalized = normalizeResponses(selectedTemplate.fields);
      const payload = { responses: normalized, status: "draft" as const };
      if (editingMeeting) {
        await updateOneOnOneMeeting(teamId, memberUserId, editingMeeting.id, payload);
      } else {
        await createOneOnOneMeeting(teamId, memberUserId, {
          templateId: selectedTemplate.id,
          ...payload,
        });
      }
      await loadMeetings();
      setView("list");
      setSelectedTemplate(null);
      setEditingMeeting(null);
      setResponses({});
      setFollowUpDrafts([]);
      setListNotice("Draft saved. Reopen it from the list when you're ready to publish.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save draft.");
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = () => {
    if (!selectedTemplate || saving) return;
    if (canCreate && isLeaderCommentsEmpty(selectedTemplate.fields, responses)) {
      setLeaderCommentsNudgeOpen(true);
      return;
    }
    setFeedbackPromptOpen(true);
  };

  const onAddLeaderNotesFromNudge = () => {
    if (!selectedTemplate) return;
    const leaderField = findLeaderCommentsField(selectedTemplate.fields);
    setLeaderCommentsNudgeOpen(false);
    if (!leaderField) return;
    setHighlightLeaderFieldId(leaderField.id);
    window.setTimeout(() => {
      document.getElementById(`check-in-field-${leaderField.id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 50);
  };

  const onContinueWithoutLeaderNotes = () => {
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(true);
  };

  const onSaveDraftClick = () => {
    if (!selectedTemplate || saving) return;
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(false);
    void performSaveDraft();
  };

  const renderLeaderCommentsNudgeModal = () => {
    if (!leaderCommentsNudgeOpen) return null;
    return (
      <div
        className="enterprise-modal-backdrop enterprise-oneone-feedback-prompt-backdrop"
        role="presentation"
        onClick={() => setLeaderCommentsNudgeOpen(false)}
      >
        <div
          className="enterprise-modal-sheet enterprise-oneone-feedback-prompt-modal"
          role="dialog"
          aria-labelledby="oneone-leader-notes-nudge-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="oneone-leader-notes-nudge-title" className="enterprise-oneone-feedback-prompt-title">
            {LEADER_COMMENTS_NUDGE_TITLE}
          </h2>
          <p className="enterprise-oneone-feedback-prompt-copy">{LEADER_COMMENTS_NUDGE_COPY}</p>
          <div className="enterprise-oneone-feedback-prompt-actions">
            <button
              type="button"
              className="enterprise-profile-cancel-btn"
              disabled={saving}
              onClick={onContinueWithoutLeaderNotes}
            >
              Continue without notes
            </button>
            <button
              type="button"
              className="enterprise-oneone-templates-primary-btn"
              disabled={saving}
              onClick={onAddLeaderNotesFromNudge}
            >
              Add leader notes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackPromptModal = () => {
    if (!feedbackPromptOpen) return null;
    return (
      <div
        className="enterprise-modal-backdrop enterprise-oneone-feedback-prompt-backdrop"
        role="presentation"
        onClick={() => setFeedbackPromptOpen(false)}
      >
        <div
          className="enterprise-modal-sheet enterprise-oneone-feedback-prompt-modal"
          role="dialog"
          aria-labelledby="oneone-feedback-prompt-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="oneone-feedback-prompt-title" className="enterprise-oneone-feedback-prompt-title">
            Request feedback?
          </h2>
          <p className="enterprise-oneone-feedback-prompt-copy">
            Request associate feedback and commitments from <strong>{memberName}</strong>?
          </p>
          <p className="enterprise-muted enterprise-oneone-feedback-prompt-sub">
            If yes, they&apos;ll get a gentle nudge to add their takeaways or skip for now.
          </p>
          <div className="enterprise-oneone-feedback-prompt-actions">
            <button
              type="button"
              className="enterprise-profile-cancel-btn"
              disabled={saving}
              onClick={() => void performSave(false)}
            >
              No
            </button>
            <button
              type="button"
              className="enterprise-oneone-templates-primary-btn"
              disabled={saving}
              onClick={() => void performSave(true)}
            >
              {saving ? "Saving…" : "Yes"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const onDelete = async (meeting: OneOnOneMeeting) => {
    if (!window.confirm(`Delete this check-in from ${formatMeetingDate(oneOnOneDisplayDate(meeting))}? This cannot be undone.`)) {
      return;
    }
    setErr(null);
    try {
      await deleteOneOnOneMeeting(teamId, memberUserId, meeting.id);
      if (previewMeeting?.id === meeting.id) setPreviewMeeting(null);
      if (menuMeetingId === meeting.id) setMenuMeetingId(null);
      if (editingMeeting?.id === meeting.id) {
        setEditingMeeting(null);
        setSelectedTemplate(null);
        setView("list");
      }
      await loadMeetings();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not delete check-in.");
    }
  };

  const checkInExportOptions = (meeting: OneOnOneMeeting) => ({
    meeting,
    memberName,
    managerName,
    meetingNumber: meetingNumberFor(meetings, meeting.id),
  });

  const onPrint = (meeting: OneOnOneMeeting) => {
    if (!canPrintCheckIn(meeting)) {
      setErr("Publish this check-in before printing.");
      return;
    }
    try {
      printOneOnOneMeeting(checkInExportOptions(meeting));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not open print view.");
    }
  };

  const onDownloadPdf = async (meeting: OneOnOneMeeting) => {
    if (!canPrintCheckIn(meeting)) {
      setErr("Publish this check-in before downloading a PDF.");
      return;
    }
    setMenuMeetingId(null);
    setDownloadingPdfId(meeting.id);
    setErr(null);
    try {
      await downloadOneOnOneMeetingPdf(checkInExportOptions(meeting));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not download PDF.");
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const renderFieldInput = (field: OneOnOneTemplateField) => {
    const value = responses[field.id] ?? "";
    const isLong = field.type === "long_text" || field.type === "manager_notes";

    if (field.type === "rating") {
      const max = field.ratingMax ?? 5;
      const current = typeof value === "number" ? value : Number(value) || 0;
      return (
        <div className="enterprise-oneone-rating-row">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={`enterprise-oneone-rating-btn${current === n ? " enterprise-oneone-rating-btn--active" : ""}`}
              onClick={() => setFieldValue(field.id, n)}
            >
              {n}
            </button>
          ))}
        </div>
      );
    }

    if (field.type === "yes_no") {
      const current = String(value).toLowerCase();
      return (
        <div className="enterprise-oneone-yesno-row">
          {(["yes", "no"] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`enterprise-oneone-yesno-btn${current === option ? " enterprise-oneone-yesno-btn--active" : ""}`}
              onClick={() => setFieldValue(field.id, option)}
            >
              {option === "yes" ? "Yes" : "No"}
            </button>
          ))}
        </div>
      );
    }

    if (isLong) {
      return (
        <AutoResizeTextarea
          className="auth-input enterprise-oneone-fill-textarea enterprise-oneone-fill-textarea--long"
          minRows={3}
          value={String(value)}
          onChange={(e) => setFieldValue(field.id, e.target.value)}
          onFocus={() => setSenecaFocusedFieldId(field.id)}
          placeholder={`Enter ${field.label.toLowerCase()}…`}
        />
      );
    }

    return (
      <AutoResizeTextarea
        className="auth-input enterprise-oneone-fill-textarea enterprise-oneone-fill-textarea--short"
        minRows={1}
        value={String(value)}
        onChange={(e) => setFieldValue(field.id, e.target.value)}
        onFocus={() => setSenecaFocusedFieldId(field.id)}
        placeholder={`Enter ${field.label.toLowerCase()}…`}
      />
    );
  };

  const exitFill = () => {
    if (editingMeeting) {
      setView("list");
      setEditingMeeting(null);
      setSelectedTemplate(null);
    } else {
      setView("pick");
      setSelectedTemplate(null);
    }
    setFollowUpDrafts([]);
    setPrepAcknowledged(false);
    setSenecaPrep(null);
    setSenecaPrepErr(null);
    setSenecaPrepLoading(false);
    setSenecaPrepRequested(false);
    setErr(null);
    setLeaderCommentsNudgeOpen(false);
    setFeedbackPromptOpen(false);
  };

  const addFollowUpDraft = () => {
    setFollowUpDrafts((prev) => [...prev, newFollowUpDraft()]);
  };

  const updateFollowUpDraft = (id: string, patch: Partial<FollowUpDraft>) => {
    setFollowUpDrafts((prev) => prev.map((draft) => (draft.id === id ? { ...draft, ...patch } : draft)));
  };

  const removeFollowUpDraft = (id: string) => {
    setFollowUpDrafts((prev) => prev.filter((draft) => draft.id !== id));
  };

  const addSenecaFollowUpTasks = (tasks: SenecaFollowUpSuggestion[]) => {
    setFollowUpDrafts((prev) => [
      ...prev,
      ...tasks.map((t) => ({
        id: crypto.randomUUID(),
        title: t.title,
        assigneeRole: t.assigneeRole,
        dueDate: t.dueDate?.slice(0, 10) ?? "",
      })),
    ]);
  };

  const openSenecaDevPlan = (draft: SenecaDevelopmentGoalDraft) => {
    setSenecaDevPlanDraft(draft);
    setSenecaDevPlanOpen(true);
  };

  const finishCheckInToList = () => {
    setView("list");
    setSelectedTemplate(null);
    setEditingMeeting(null);
    setResponses({});
    setFollowUpDrafts([]);
    setListNotice(null);
    setSenecaSummaryOpen(false);
    setSenecaSummaryPayload(null);
  };

  const renderFollowUpTaskList = (tasks: NonNullable<OneOnOneMeeting["followUpTasks"]>) => (
    <div className="enterprise-oneone-followup-list">
      <h4 className="enterprise-oneone-followup-list-title">Follow-up tasks</h4>
      <ul className="enterprise-oneone-followup-items">
        {tasks.map((task) => {
          const dueLabel = formatFollowUpDueDate(task.dueDate);
          const isDone = task.status === "done";
          const statusLabel =
            task.status !== "todo" && !isDone ? ` · ${task.status.replace("_", " ")}` : "";
          return (
            <li key={task.id} className="enterprise-oneone-followup-item">
              <div className="enterprise-oneone-followup-item-body">
                <span className="enterprise-oneone-followup-item-title">{task.title}</span>
                <span className="enterprise-oneone-followup-item-meta">
                  Assigned to {assigneeDisplayName(task.assignee?.id, memberUserId, memberName, leaderUserId, managerName)}
                  {dueLabel ? ` · Due ${dueLabel}` : ""}
                  {statusLabel}
                </span>
              </div>
              <span
                className={`enterprise-oneone-followup-check${
                  isDone ? " enterprise-oneone-followup-check--done" : " enterprise-oneone-followup-check--open"
                }`}
                aria-label={isDone ? "Complete" : "Incomplete"}
                role="img"
              />
            </li>
          );
        })}
      </ul>
    </div>
  );

  const renderMeetingPreviewBody = (meeting: OneOnOneMeeting) => {
    const groups = groupPreviewSections(meeting.templateFields);
    const associateAnswer = meeting.responses[ASSOCIATE_FEEDBACK_FIELD_ID];
    const showAssociateFeedback =
      (associateAnswer !== undefined && associateAnswer !== "" && associateAnswer !== 0) ||
      meeting.associateFeedbackPending;

    return (
      <>
        <div className="enterprise-oneone-preview-sections">
          {groups.map((group) => {
            const questions = group.fields
              .map((field) => renderPreviewQuestion(field, meeting.responses[field.id]))
              .filter(Boolean);
            if (questions.length === 0) return null;
            return (
              <section key={group.section.id} className="enterprise-oneone-preview-section-block">
                <h3 className="enterprise-oneone-preview-section-heading">{group.section.label}</h3>
                <div className="enterprise-oneone-preview-section-content">{questions}</div>
              </section>
            );
          })}
          {showAssociateFeedback ? (
            <section className="enterprise-oneone-preview-section-block">
              <h3 className="enterprise-oneone-preview-section-heading">{ASSOCIATE_FEEDBACK_LABEL}</h3>
              <div className="enterprise-oneone-preview-section-content">
                {associateAnswer !== undefined && associateAnswer !== "" && associateAnswer !== 0 ? (
                  <div className="enterprise-oneone-preview-question">
                    <div className="enterprise-oneone-preview-question-answer">
                      {formatAssociateResponseDisplay(associateAnswer)}
                    </div>
                  </div>
                ) : (
                  <div className="enterprise-oneone-preview-question">
                    <div className="enterprise-oneone-preview-question-answer enterprise-muted">
                      Awaiting associate feedback
                    </div>
                  </div>
                )}
              </div>
            </section>
          ) : null}
        </div>
        {meeting.followUpTasks?.length ? renderFollowUpTaskList(meeting.followUpTasks) : null}
      </>
    );
  };

  const renderMeetingPreviewModal = () => {
    if (!previewMeeting) return null;
    const meetingNum = meetingNumberFor(meetings, previewMeeting.id);
    return (
      <div
        className="enterprise-modal-backdrop enterprise-oneone-preview-backdrop"
        role="presentation"
        onClick={() => setPreviewMeeting(null)}
      >
        <div
          className="enterprise-modal-sheet enterprise-oneone-preview-modal"
          role="dialog"
          aria-label={`${previewMeeting.templateTitle} check-in`}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="enterprise-oneone-preview-header">
            <div className="enterprise-oneone-preview-header-text">
              <p className="enterprise-oneone-templates-kicker">Check-in</p>
              <h2 className="enterprise-oneone-preview-title">{previewMeeting.templateTitle}</h2>
              <p className="enterprise-oneone-preview-meta">
                {formatMeetingDate(oneOnOneDisplayDate(previewMeeting))} · Check-in #{meetingNum}
                {managerName ? ` · Manager: ${managerName}` : ""}
              </p>
              <div className="enterprise-oneone-preview-status">
                <MeetingStatusBadge meeting={previewMeeting} />
              </div>
            </div>
            <div className="enterprise-oneone-preview-header-actions">
              {canPrintCheckIn(previewMeeting) ? (
                <>
                  <button
                    type="button"
                    className="enterprise-dev-plan-print-btn"
                    onClick={() => onPrint(previewMeeting)}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="enterprise-dev-plan-print-btn"
                    disabled={downloadingPdfId === previewMeeting.id}
                    onClick={() => void onDownloadPdf(previewMeeting)}
                  >
                    {downloadingPdfId === previewMeeting.id ? "Downloading…" : "Download PDF"}
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className="enterprise-oneone-templates-close"
                aria-label="Close preview"
                onClick={() => setPreviewMeeting(null)}
              >
                ×
              </button>
            </div>
          </header>
          <div className="enterprise-oneone-preview-body">{renderMeetingPreviewBody(previewMeeting)}</div>
        </div>
      </div>
    );
  };

  if (view === "pick") {
    return renderCheckInShell(
      <>
        {err ? <p className="enterprise-form-error" role="alert">{err}</p> : null}
        {loadingTemplates ? (
          <p className="enterprise-muted enterprise-oneone-history-loading">Loading templates…</p>
        ) : null}
        {!loadingTemplates && templates.length > 0 ? (
          <div className="enterprise-oneone-history-table-wrap">
            <div className="enterprise-oneone-history-table-head" aria-hidden>
              <span>Template</span>
              <span>Questions</span>
              <span />
            </div>
            <ul className="enterprise-oneone-template-pick-list">
              {templates.map((template) => {
                const questionCount = template.fields.filter((f) => f.type !== "section").length;
                return (
                  <li key={template.id}>
                    <button type="button" className="enterprise-oneone-template-pick-row" onClick={() => pickTemplate(template)}>
                      <span className="enterprise-oneone-template-pick-name">{template.title}</span>
                      <span className="enterprise-oneone-template-pick-meta">
                        {questionCount} question{questionCount !== 1 ? "s" : ""}
                      </span>
                      <span className="enterprise-oneone-template-pick-chevron" aria-hidden>▸</span>
                    </button>
                    {template.description ? (
                      <p className="enterprise-oneone-template-pick-desc">{template.description}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {!loadingTemplates && templates.length === 0 && !err ? (
          <CheckInGrowCard
            canCreate={false}
            title="No templates yet"
            copy="Ask your workspace owner to create check-in templates from the Team page."
          />
        ) : null}
      </>,
      {
        title: "Choose a template",
        subtitle: `Select a template to fill out for ${memberName}.`,
        backLabel: "Back to history",
        onBack: () => {
          setView("list");
          setErr(null);
        },
      },
    );
  }

  if (view === "fill" && selectedTemplate) {
    const sortedFields = [...selectedTemplate.fields].sort((a, b) => a.order - b.order);
    const existingFollowUps = editingMeeting?.followUpTasks ?? [];
    const leaderLabel = managerName ?? "Leader";
    const leaderPrepItems = (selectedTemplate.leaderPrep ?? []).map((item) => item.trim()).filter(Boolean);
    const showPrepGate = !editingMeeting && !prepAcknowledged && canCreate && leaderPrepItems.length > 0;

    if (showPrepGate) {
      return renderCheckInShell(
        <div className="seneca-checkin-layout seneca-checkin-layout--prep">
          <SenecaPrepCard
            teamId={teamId}
            memberUserId={memberUserId}
            memberName={memberName}
            managerName={managerName}
            templateId={selectedTemplate.id}
            templateLeaderPrep={leaderPrepItems}
            prep={senecaPrep}
            loading={senecaPrepLoading}
            err={senecaPrepErr}
            prepRequested={senecaPrepRequested}
            onRequestPrep={requestSenecaPrep}
          />
        </div>,
        {
          title: selectedTemplate.title,
          subtitle: `Before your check-in with ${memberName}${leaderPrepItems.length ? " · review leader prep below" : ""}`,
          backLabel: "Choose another template",
          onBack: exitFill,
          footer: (
            <div className="enterprise-oneone-fill-actions">
              <button
                type="button"
                className="enterprise-oneone-templates-primary-btn enterprise-oneone-fill-save"
                onClick={() => setPrepAcknowledged(true)}
              >
                Start check-in
              </button>
            </div>
          ),
        },
      );
    }

    const fillTitle = editingMeeting
      ? editingMeeting.status === "draft"
        ? "Resume editing"
        : "Edit check-in"
      : selectedTemplate.title;
    const fillSubtitle = editingMeeting
      ? `${selectedTemplate.title} · ${formatMeetingDate(oneOnOneDisplayDate(editingMeeting))}`
      : selectedTemplate.description ?? undefined;
    const showSaveDraft = !editingMeeting || editingMeeting.status === "draft";
    const publishLabel = editingMeeting?.status === "draft" ? "Publish check-in" : editingMeeting ? "Save changes" : "Save check-in";

    const senecaPanel = canCreate ? (
      <SenecaCheckInPanel
        teamId={teamId}
        memberUserId={memberUserId}
        memberName={memberName}
        managerName={managerName}
        template={selectedTemplate}
        responses={responses}
        focusFieldId={senecaFocusedFieldId}
        placement="header"
        onApplyText={(fieldId, text) => {
          setFieldValue(fieldId, text);
          setSenecaFocusedFieldId(fieldId);
          requestAnimationFrame(() => {
            document.getElementById(`check-in-field-${fieldId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
          });
        }}
        onAddFollowUpTasks={addSenecaFollowUpTasks}
        onSuggestDevelopmentGoal={openSenecaDevPlan}
      />
    ) : null;

    return renderCheckInShell(
      <>
        {err ? <p className="enterprise-form-error" role="alert">{err}</p> : null}
        <div className="seneca-checkin-layout seneca-checkin-layout--wide">
          {canCreate && !editingMeeting ? (
            <SenecaPrepCard
              teamId={teamId}
              memberUserId={memberUserId}
              memberName={memberName}
              managerName={managerName}
              templateId={selectedTemplate.id}
              templateLeaderPrep={leaderPrepItems}
              prep={senecaPrep}
              loading={senecaPrepLoading}
              err={senecaPrepErr}
              prepRequested={senecaPrepRequested}
              onRequestPrep={requestSenecaPrep}
              compact
            />
          ) : null}
          <ul className="enterprise-oneone-fill-fields">
              {sortedFields.map((field) =>
                field.type === "associate_notes" ? null : field.type === "section" ? (
                  <li key={field.id} className="enterprise-oneone-fill-section">
                    <h4>{field.label}</h4>
                  </li>
                ) : (
                  <li
                    key={field.id}
                    id={`check-in-field-${field.id}`}
                    className={`enterprise-oneone-fill-field${
                      highlightLeaderFieldId === field.id ? " enterprise-oneone-fill-field--nudge" : ""
                    }`}
                  >
                    <label className="enterprise-oneone-fill-label">
                      {field.label}
                      {field.required ? <span className="enterprise-oneone-fill-required">Required</span> : null}
                      <span className="enterprise-oneone-fill-type">{fieldTypeLabel(field.type)}</span>
                    </label>
                    {field.helpText ? <p className="enterprise-muted enterprise-oneone-fill-help">{field.helpText}</p> : null}
                    {renderFieldInput(field)}
                  </li>
                ),
              )}
            </ul>
            <section className="enterprise-oneone-followup">
              <div className="enterprise-oneone-followup-head">
                <div>
                  <h4 className="enterprise-oneone-followup-title">Follow-up tasks</h4>
                  <p className="enterprise-muted enterprise-oneone-followup-sub">
                    Optional. Creates real workspace tasks assigned to the leader or associate.
                  </p>
                </div>
                <button type="button" className="enterprise-oneone-templates-pane-btn" onClick={addFollowUpDraft}>
                  Add task
                </button>
              </div>
              {existingFollowUps.length > 0 ? renderFollowUpTaskList(existingFollowUps) : null}
              {followUpDrafts.length > 0 ? (
                <ul className="enterprise-oneone-followup-drafts">
                  {followUpDrafts.map((draft) => (
                    <li key={draft.id} className="enterprise-oneone-followup-draft">
                      <input
                        type="text"
                        className="auth-input enterprise-oneone-followup-input"
                        value={draft.title}
                        onChange={(e) => updateFollowUpDraft(draft.id, { title: e.target.value })}
                        placeholder="Task title"
                        aria-label="Follow-up task title"
                      />
                      <select
                        className="auth-input enterprise-oneone-followup-select"
                        value={draft.assigneeRole}
                        onChange={(e) =>
                          updateFollowUpDraft(draft.id, {
                            assigneeRole: e.target.value as FollowUpDraft["assigneeRole"],
                          })
                        }
                        aria-label="Assign follow-up task to"
                      >
                        <option value="associate">Associate · {memberName}</option>
                        <option value="leader">Leader · {leaderLabel}</option>
                      </select>
                      <input
                        type="date"
                        className="auth-input enterprise-oneone-followup-date"
                        value={draft.dueDate}
                        onChange={(e) => updateFollowUpDraft(draft.id, { dueDate: e.target.value })}
                        aria-label="Follow-up task due date"
                      />
                      <button
                        type="button"
                        className="enterprise-oneone-templates-table-action enterprise-oneone-templates-table-action--danger"
                        onClick={() => removeFollowUpDraft(draft.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : existingFollowUps.length === 0 && followUpDrafts.length === 0 ? (
                <p className="enterprise-muted enterprise-oneone-followup-empty">No follow-up tasks yet.</p>
              ) : null}
            </section>
        </div>
        {renderLeaderCommentsNudgeModal()}
        {renderFeedbackPromptModal()}
      </>,
      {
        title: fillTitle,
        subtitle: fillSubtitle,
        backLabel: editingMeeting ? "Back to history" : "Choose another template",
        onBack: exitFill,
        headerAside: senecaPanel,
        footer: (
          <div className="enterprise-oneone-fill-actions enterprise-oneone-fill-actions--multi">
            <button type="button" className="enterprise-profile-cancel-btn" disabled={saving} onClick={() => setView("list")}>
              Cancel
            </button>
            {showSaveDraft ? (
              <button
                type="button"
                className="enterprise-oneone-fill-draft-btn"
                disabled={saving}
                onClick={onSaveDraftClick}
              >
                {saving ? "Saving…" : "Save draft"}
              </button>
            ) : null}
            <button
              type="button"
              className="enterprise-oneone-templates-primary-btn enterprise-oneone-fill-save"
              disabled={saving}
              onClick={onSaveClick}
            >
              {saving ? "Saving…" : publishLabel}
            </button>
          </div>
        ),
      },
    );
  }

  const publishedMeetings = meetings.filter((m) => m.status !== "draft");
  const latestMeeting =
    meetings.length === 0
      ? null
      : [...meetings].sort((a, b) => oneOnOneDisplayDateMs(b) - oneOnOneDisplayDateMs(a))[0] ?? null;
  const latestExportable = publishedMeetings.find((m) => canPrintCheckIn(m)) ?? null;
  const derivedGoalsActive = activeDevGoals ?? 0;
  const derivedGoalsCompleted = completedDevGoals ?? 0;
  const feedbackReceived = countFeedbackThisMonth(meetings);
  const lastCheckInPrimary = latestMeeting
    ? formatRelativeDaysAgo(oneOnOneDisplayDate(latestMeeting))
    : daysSinceLastCheckIn != null
      ? formatRelativeDaysAgo(
          new Date(Date.now() - daysSinceLastCheckIn * 86_400_000).toISOString(),
        )
      : "No check-in yet";
  const lastCheckInHint = latestMeeting
    ? formatMeetingDate(oneOnOneDisplayDate(latestMeeting))
    : "—";
  const nextDueDisplay = nextCheckInValue ?? "—";
  const nextDueHint = nextCheckInHint?.trim() || "Based on workplace standards";
  const feedbackPrimary = `${feedbackReceived} this month`;
  const feedbackHint = feedbackReceived > 0 ? `+${feedbackReceived} this month` : "No feedback yet";
  const nowForQuarter = new Date();
  const quarterStart = new Date(
    nowForQuarter.getFullYear(),
    Math.floor(nowForQuarter.getMonth() / 3) * 3,
    1,
  );
  const quarterCount = publishedMeetings.filter((m) => {
    try {
      return new Date(oneOnOneDisplayDate(m)) >= quarterStart;
    } catch {
      return false;
    }
  }).length;
  const onTrackCount = publishedMeetings.filter((m) => getOneOnOneMeetingStatusFromMeeting(m) !== "open").length;
  const onTimePct =
    publishedMeetings.length === 0
      ? 0
      : Math.round((onTrackCount / publishedMeetings.length) * 100);
  const topFocus =
    publishedMeetings[0]?.templateTitle?.trim() ||
    (teamName ? `Growing with ${teamName}` : "Team Development");

  const onExportLatest = () => {
    if (!latestExportable) return;
    void onDownloadPdf(latestExportable);
  };

  return (
    <div className="enterprise-oneone-history enterprise-oneone-history--scrollable enterprise-checkins-page">
      <div className="enterprise-checkins-metrics" aria-label="Check-in metrics">
        <div className="enterprise-checkins-metric enterprise-checkins-metric--green">
          <span className="enterprise-checkins-metric-icon" aria-hidden>
            <IconMetricCalendar />
          </span>
          <span className="enterprise-checkins-metric-label">Last Check-in</span>
          <strong className="enterprise-checkins-metric-value">{lastCheckInPrimary}</strong>
          <span className="enterprise-checkins-metric-hint">{lastCheckInHint}</span>
        </div>
        <div className="enterprise-checkins-metric enterprise-checkins-metric--blue">
          <span className="enterprise-checkins-metric-icon" aria-hidden>
            <IconMetricTarget />
          </span>
          <span className="enterprise-checkins-metric-label">Goals</span>
          <strong className="enterprise-checkins-metric-value">
            {derivedGoalsActive}
            <span className="enterprise-checkins-metric-suffix"> active</span>
          </strong>
          <span className="enterprise-checkins-metric-hint">
            {derivedGoalsCompleted} completed
          </span>
        </div>
        <div className="enterprise-checkins-metric enterprise-checkins-metric--orange">
          <span className="enterprise-checkins-metric-icon" aria-hidden>
            <IconMetricCalendar />
          </span>
          <span className="enterprise-checkins-metric-label">Next Check-in Due</span>
          <strong className="enterprise-checkins-metric-value">{nextDueDisplay}</strong>
          <span className="enterprise-checkins-metric-hint">{nextDueHint}</span>
        </div>
        <div className="enterprise-checkins-metric enterprise-checkins-metric--blue">
          <span className="enterprise-checkins-metric-icon" aria-hidden>
            <IconMetricChat />
          </span>
          <span className="enterprise-checkins-metric-label">Feedback Received</span>
          <strong className="enterprise-checkins-metric-value">{feedbackPrimary}</strong>
          <span className="enterprise-checkins-metric-hint enterprise-checkins-metric-hint--positive">
            {feedbackHint}
          </span>
        </div>
      </div>

      {listNotice ? (
        <p className="enterprise-oneone-history-notice" role="status">
          {listNotice}
        </p>
      ) : null}
      {err ? <p className="enterprise-form-error" role="alert">{err}</p> : null}

      <div className="enterprise-checkins-main-grid">
        <div className="enterprise-checkins-history-card">
          <div className="enterprise-checkins-history-card-head">
            <div>
              <h4 className="enterprise-checkins-history-title">Check-in history</h4>
              <p className="enterprise-checkins-history-sub">
                {isSelf
                  ? "Your recorded check-ins with this team."
                  : `Recorded check-ins with ${memberName}.`}
              </p>
            </div>
            <div className="enterprise-checkins-history-controls">
              <button
                type="button"
                className="enterprise-checkins-export-btn enterprise-checkins-export-btn--sm"
                disabled={!latestExportable || downloadingPdfId === latestExportable?.id}
                onClick={onExportLatest}
              >
                <IconExport />
                {downloadingPdfId && latestExportable && downloadingPdfId === latestExportable.id
                  ? "Exporting…"
                  : "Export"}
              </button>
              <select className="enterprise-checkins-filter" aria-label="Filter check-ins" defaultValue="all">
                <option value="all">All check-ins</option>
              </select>
              {canCreate ? (
                <button
                  type="button"
                  className="enterprise-checkins-new-btn enterprise-checkins-new-btn--sm"
                  disabled={loadingTemplates}
                  onClick={() => void startCreate()}
                >
                  {loadingTemplates ? "Loading…" : "+ New check-in"}
                  <span className="enterprise-checkins-new-btn-chevron" aria-hidden>
                    ▾
                  </span>
                </button>
              ) : null}
            </div>
          </div>

          {loadingMeetings && meetings.length === 0 ? (
            <EnterprisePageLoading
              label="Loading check-ins"
              className="enterprise-profile-section-loading"
              testId="check-ins-loading"
            />
          ) : meetings.length === 0 ? (
            <CheckInGrowCard
              canCreate={canCreate}
              loading={loadingTemplates}
              onCreate={() => void startCreate()}
            />
          ) : null}

          {meetings.length > 0 ? (
            <ul className="enterprise-checkins-history-list">
              {meetings.map((meeting) => {
                const dateIso = oneOnOneDisplayDate(meeting);
                const byName =
                  meeting.createdBy?.name?.trim() ||
                  managerName ||
                  "Manager";
                return (
                  <li key={meeting.id} className="enterprise-checkins-history-row">
                    <button
                      type="button"
                      className="enterprise-checkins-history-row-main"
                      onClick={() => {
                        setMenuMeetingId(null);
                        setPreviewMeeting(meeting);
                      }}
                    >
                      <IconHistoryCheck
                        attention={
                          meeting.status !== "draft" &&
                          getOneOnOneMeetingStatusFromMeeting(meeting) === "open"
                        }
                      />
                      <div className="enterprise-checkins-history-row-primary">
                        <span className="enterprise-checkins-history-date">{formatMeetingDate(dateIso)}</span>
                        <span className="enterprise-checkins-history-relative">
                          {formatRelativeDaysAgo(dateIso)}
                        </span>
                      </div>
                      <div className="enterprise-checkins-history-row-secondary">
                        <span className="enterprise-checkins-history-goals">
                          {goalsReviewedLabel(meeting)}
                        </span>
                        <span className="enterprise-checkins-history-by">By {byName}</span>
                      </div>
                      <CheckInListStatusBadge meeting={meeting} />
                      <span className="enterprise-checkins-history-chevron" aria-hidden>
                        <IconChevronRight />
                      </span>
                    </button>
                    {canModify || canPrintCheckIn(meeting) ? (
                      <div className="enterprise-checkins-history-row-menu-wrap">
                        <button
                          type="button"
                          className="enterprise-checkins-history-row-menu-btn"
                          aria-label={`Actions for ${meeting.templateTitle}`}
                          aria-expanded={menuMeetingId === meeting.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuMeetingId((current) => (current === meeting.id ? null : meeting.id));
                          }}
                        >
                          ⋯
                        </button>
                        {menuMeetingId === meeting.id ? (
                          <div className="enterprise-checkins-history-row-menu" role="menu">
                            {canPrintCheckIn(meeting) ? (
                              <>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuMeetingId(null);
                                    onPrint(meeting);
                                  }}
                                >
                                  Print
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  disabled={downloadingPdfId === meeting.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void onDownloadPdf(meeting);
                                  }}
                                >
                                  {downloadingPdfId === meeting.id ? "Downloading…" : "Download PDF"}
                                </button>
                              </>
                            ) : null}
                            {canModify ? (
                              <>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(meeting);
                                  }}
                                >
                                  {checkInEditMenuLabel(meeting)}
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="enterprise-checkins-history-row-menu-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuMeetingId(null);
                                    void onDelete(meeting);
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {meetings.length > 0 ? (
            <div className="enterprise-checkins-history-footer">
              Showing {meetings.length} of {meetings.length} check-ins
            </div>
          ) : null}

        </div>

        <aside className="enterprise-checkins-aside">
          <div className="enterprise-checkins-why">
            <div className="enterprise-checkins-aside-title-row">
              <span className="enterprise-checkins-aside-icon" aria-hidden>
                <IconWhyStar />
              </span>
              <h4 className="enterprise-checkins-aside-title">Why check in?</h4>
            </div>
            <ul className="enterprise-checkins-why-list">
              <li>Track progress on your goals</li>
              <li>Celebrate wins and improve</li>
              <li>Share feedback with your team</li>
              <li>Build consistency and trust</li>
            </ul>
          </div>
          <div className="enterprise-checkins-summary">
            <div className="enterprise-checkins-aside-title-row">
              <span className="enterprise-checkins-aside-icon enterprise-checkins-aside-icon--calendar" aria-hidden>
                <IconCheckinsHeader />
              </span>
              <div>
                <h4 className="enterprise-checkins-aside-title">Check-in summary</h4>
                <p className="enterprise-checkins-summary-sub">Your consistency at a glance</p>
              </div>
            </div>
            <ul className="enterprise-checkins-summary-list">
              <li>
                <span className="enterprise-checkins-summary-icon" aria-hidden>
                  <IconSummaryChart />
                </span>
                <span>
                  <strong>{quarterCount}</strong> Check-ins this quarter
                </span>
              </li>
              <li>
                <span className="enterprise-checkins-summary-icon" aria-hidden>
                  <IconSummaryClock />
                </span>
                <span>
                  <strong>{onTimePct}%</strong> Completed on time
                </span>
              </li>
              <li>
                <span className="enterprise-checkins-summary-icon" aria-hidden>
                  <IconSummaryPeople />
                </span>
                <span>
                  Top focus <strong>{topFocus}</strong>
                </span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {renderMeetingPreviewModal()}
      {senecaSummaryOpen && senecaSummaryPayload ? (
        <SenecaSummaryModal
          open={senecaSummaryOpen}
          teamId={teamId}
          memberUserId={memberUserId}
          memberName={memberName}
          managerName={managerName}
          templateTitle={senecaSummaryPayload.templateTitle}
          templateFields={senecaSummaryPayload.templateFields}
          responses={senecaSummaryPayload.responses}
          followUpTasks={senecaSummaryPayload.followUpTasks}
          onClose={finishCheckInToList}
          onAddFollowUpTasks={() => {
            /* saved check-in already published; suggestions are for manager reference */
          }}
          onCreateDevelopmentGoal={openSenecaDevPlan}
        />
      ) : null}
      {senecaDevPlanOpen ? (
        <DevelopmentPlanGenerator
          teamId={teamId}
          memberUserId={memberUserId}
          memberName={memberName}
          managerName={managerName}
          initialDraft={senecaDevPlanDraft}
          onCreated={() => void loadMeetings()}
          onClose={() => {
            setSenecaDevPlanOpen(false);
            setSenecaDevPlanDraft(null);
          }}
        />
      ) : null}
    </div>
  );
}
